'use client';

import { PublicUserDocument } from '@models/user';
import Input from '@ui/Input';
import { Settings } from 'lucide-react';
import { ProductDocument, Variation } from 'models/product';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';
import BuyAddOfferButtons from './BuyAddOfferButtons';

const BuyerActionButtons = ({
  product,
  seller,
  selectedVariations,
  setErrors,
  hideOffer,
  onCustomInputsChange,
  dismissQuickView,
  isSidebar,
}: {
  product: ProductDocument;
  seller?: PublicUserDocument | null;
  selectedVariations: Variation[] | null;
  setErrors?: React.Dispatch<React.SetStateAction<string[]>>;
  hideOffer?: boolean;
  onCustomInputsChange?: (
    enabled: boolean,
    values?: Record<string, string>
  ) => void;
  dismissQuickView?: () => void;
  isSidebar?: boolean;
}) => {
  const [customInputsEnabled, setCustomInputsEnabled] = useState(false);
  const [customInputValues, setCustomInputValues] = useState<
    Record<string, string>
  >({});
  const pathname = usePathname();

  const handleCustomInputChange = (fieldId: string, value: string) => {
    const newValues = { ...customInputValues, [fieldId]: value };
    setCustomInputValues(newValues);
    onCustomInputsChange?.(customInputsEnabled, newValues);
  };

  return (
    <>
      <div className="flex flex-col gap-[1.6rem]">
        {/* find the quantity in stock for the given variations selected */}

        {product.custom_inputs && (
          <div className="relative flex min-h-[8rem] w-full flex-col rounded-b-[1rem] border-b-2 border-l-2 border-r-2 border-t-2 border-b-gray-300 border-l-gray-300 border-r-gray-300 border-t-brand-secondary px-4 pb-4 pt-4">
            <div
              className="flex w-full cursor-pointer items-center justify-between gap-x-4"
              onClick={() => {
                setCustomInputsEnabled(!customInputsEnabled);
                onCustomInputsChange?.(!customInputsEnabled, customInputValues);
              }}
            >
              <div className="flex items-center gap-x-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-yellow-400">
                  <Settings className="h-8 w-8 text-white" strokeWidth={2} />
                </div>
                <div className="flex flex-col gap-y-1">
                  <span className="text-[1.6rem] font-bold text-black">
                    Personalize this item (+${product.custom_inputs.cost})
                  </span>
                  <span className="text-[1.6rem] text-black">
                    Add your name and number.
                  </span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={customInputsEnabled}
                onChange={(e) => {
                  setCustomInputsEnabled(e.target.checked);
                  onCustomInputsChange?.(e.target.checked, customInputValues);
                }}
                className="h-8 w-8 shrink-0 cursor-pointer rounded-md border-gray-300 text-brand-secondary focus:ring-2 focus:ring-brand-secondary"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {customInputsEnabled && (
              <div className="mt-8 flex flex-col gap-8">
                {product.custom_inputs.fields.map((field) => (
                  <div key={field.id} className="flex flex-col gap-2">
                    <label className="text-[1.6rem] font-semibold">
                      {field.label}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <Input
                      type={
                        field.type as
                          | 'text'
                          | 'number'
                          | 'email'
                          | 'tel'
                          | 'password'
                          | 'search'
                          | 'date'
                          | 'time'
                      }
                      placeholder={field.placeholder || ''}
                      maxLength={field.max_length}
                      required={field.required}
                      value={customInputValues[field.id] || ''}
                      onChange={(e) =>
                        (!field.max_length ||
                          e.target.value.length <= field.max_length) &&
                        handleCustomInputChange(field.id, e.target.value)
                      }
                      className="!rounded-[1.5rem] border border-gray-300 px-3 py-2 text-[1.3rem]"
                    />
                    {field.max_length && (
                      <div className="flex justify-end">
                        <span className="text-[1.3rem] text-gray-500">
                          {customInputValues[field.id]?.length || 0} /{' '}
                          {field.max_length}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!product.is_draft && !product.is_auction && (
          <BuyAddOfferButtons
            product={product}
            seller={seller}
            selectedVariations={selectedVariations}
            customInputs={
              customInputsEnabled
                ? {
                    enabled: true,
                    values: customInputValues,
                    cost: product.custom_inputs?.cost || 0,
                  }
                : undefined
            }
            onAddToCartError={() => {
              if (!selectedVariations || selectedVariations.length === 0) {
                setErrors?.(['Jersey', 'Pant']);
              } else if (
                product.category2 === 'Gear Combos' &&
                selectedVariations &&
                selectedVariations.length === 1
              ) {
                // find out which variation is missing
                const missingVariation = selectedVariations[0].is_number
                  ? 'Jersey'
                  : 'Pant';
                setErrors?.([missingVariation]);
              }
            }}
            hideOffer={hideOffer}
            dismissQuickView={dismissQuickView}
            // if is sidebar, pass in the my-cart page pathname to hide the action buttons (buy it now and make an offer)
            pathname={isSidebar ? '/my-cart' : pathname}
          />
        )}
      </div>
    </>
  );
};

export default BuyerActionButtons;
